import { DecimalPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CompilingWidgetService } from '../../../services/compiling/widget.service';
import {
  emptyMultiplierAnswer,
  isDefaultMultiplierOption,
  isMultiplierAnswer,
  MultiplierAnswer,
  MultiplierOption,
  MultiplierRadioButtonCustomization,
  selectMultiplierOption,
} from './multiplier-answer';

/**
 * One control holds the whole answer, `{ option, value }`. The radios and the
 * number input are bound to its two halves, never to the control itself, so
 * the default option can grey the number input out without disabling the
 * control: a disabled control would drop out of the saved form value, and
 * re-enabling it could undo the read-only lock the dashboard puts on it.
 */
@Component({
  selector: 'lib-multiplier-radiobutton-compiling',
  imports: [FormsModule, RadioButtonModule, InputNumberModule, DecimalPipe],
  template: `
    @let widget = widgetService.widget();
    @let formControl = widgetService.formControl();
    @if (widget && formControl) {
      @let answer = currentAnswer();
      @let selected = selectedOption();
      <div class="flex w-full flex-col gap-2">
        <label [for]="groupId" class="whitespace-pre-line text-justify">
          {{ widget.customization.description }} (max
          {{ widgetService.maxPoints() | number: '1.0-1' }}
          pt)
        </label>
        <div
          [class]="'p-fluid flex' + topologyStyle"
          (keydown)="onKeyDown($event)"
        >
          @for (
            option of widget.customization.options;
            track option.key;
            let i = $index
          ) {
            <div class="flex flex-row items-center gap-2 whitespace-nowrap">
              @let id = groupId + '_option_' + i;
              <p-radioButton
                [autofocus]="false"
                [inputId]="id"
                [name]="groupId"
                [value]="option.key"
                [ngModel]="answer.option"
                [ngModelOptions]="{ standalone: true }"
                [disabled]="formControl.disabled"
                (ngModelChange)="selectOption($event)"
              ></p-radioButton>
              <label [for]="id">
                {{ option.label }}
              </label>
            </div>
          }
        </div>
        <p-inputNumber
          [autofocus]="false"
          [inputId]="groupId + '_value'"
          [ngModel]="answer.value"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="setValue($event)"
          [disabled]="formControl.disabled || !selected || isDefault(selected)"
          [min]="selected?.min ?? 0"
          [max]="selected?.max"
          mode="decimal"
          [minFractionDigits]="0"
          [maxFractionDigits]="widget.customization.decimal ? 3 : 0"
          [showButtons]="true"
          (onKeyDown)="onKeyDown($event)"
          class="w-full rounded-md bg-white"
          decrementButtonClass="b-0 m-0 p-0 bg-accent"
          incrementButtonClass="b-0 m-0 p-0 bg-accent"
        />
        @if (formControl.errors?.['multiplierAnswer']; as reason) {
          <small class="text-red-500">
            @switch (reason) {
              @case ('missing') {
                Indica un valore.
              }
              @case ('out-of-range') {
                Il valore deve essere compreso tra
                {{ selected?.min ?? 0 | number: '1.0-3' }} e
                {{ selected?.max | number: '1.0-3' }}.
              }
              @case ('not-integer') {
                Il valore deve essere un numero intero.
              }
              @default {
                L'opzione scelta non è più disponibile: selezionane un'altra.
              }
            }
          </small>
        }
      </div>
    }
  `,
  styleUrls: ['../style.css'],
  providers: [],
})
export class MultiplierRadioButtonCompilingComponent {
  protected widgetService = inject(
    CompilingWidgetService<
      MultiplierRadioButtonCustomization,
      MultiplierAnswer
    >,
  );

  protected topologyStyle = ' flex-row flex-wrap gap-x-5 gap-y-2';
  protected groupId = `multiplierRadioButton_${this.widgetService.uniqueId}`;
  protected readonly isDefault = isDefaultMultiplierOption;

  protected currentAnswer(): MultiplierAnswer {
    const value = this.widgetService.formControl()?.value;
    return isMultiplierAnswer(value) ? value : emptyMultiplierAnswer();
  }

  protected selectedOption(): MultiplierOption | undefined {
    const option = this.currentAnswer().option;
    return this.widgetService
      .widget()
      ?.customization.options?.find((o: MultiplierOption) => o.key === option);
  }

  /** Default: value back to 0. Another option: the value clamped into its range. */
  protected selectOption(key: string) {
    const widget = this.widgetService.widget();
    const control = this.widgetService.formControl();
    if (!widget || !control) return;

    control.setValue(
      selectMultiplierOption(
        widget.customization.options ?? [],
        key,
        this.currentAnswer().value,
      ),
    );
    control.markAsDirty();
  }

  protected setValue(value: number | null) {
    const control = this.widgetService.formControl();
    if (!control) return;

    control.setValue({ option: this.currentAnswer().option, value });
    control.markAsDirty();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }
}
